import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoPage() {
  const { slug } = useParams();
  const [demo, setDemo] = useState(undefined);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/demos/${slug}`)
      .then(({ data }) => {
        setDemo(data);
        document.title = `${data.title} - Form Pathway`;
        if (data.status === "ready") {
          const isOwner = !!localStorage.getItem("fp_token");
          const key = `fp_viewed_${slug}`;
          if (!isOwner && !sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            axios.post(`${API}/demos/${slug}/view`).catch(() => {});
          }
        }
      })
      .catch(() => setDemo(null));
  }, [slug]);

  if (demo === undefined)
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (demo === null)
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 text-center" data-testid="demo-not-found">
        <p className="overline mb-5">Form Pathway</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl">This screening isn't available.</h1>
        <p className="mt-5 text-muted-foreground max-w-md">
          The link may be incorrect or the demo has been removed.
        </p>
        <a
          href="/"
          className="mt-10 inline-flex bg-[#1A1A1A] text-[#F7F5F0] px-8 py-3 text-xs tracking-[0.18em] uppercase hover:bg-accent transition-colors"
        >
          Visit Form Pathway
        </a>
      </div>
    );

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="demo-page">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="font-display text-xl tracking-tight">
            Form Pathway<span className="text-accent">.</span>
          </a>
          <a
            href="/#contact"
            className="text-xs tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors"
          >
            Enquire
          </a>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-14 sm:py-20">
        <p className="overline mb-5" data-testid="demo-overline">Private Screening</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="demo-title">
          {demo.title}
        </h1>
        {demo.message && (
          <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="demo-message">
            {demo.message}
          </p>
        )}

        <div className="mt-12 sm:mt-16">
          {demo.status === "ready" ? (
            <div
              className="mx-auto bg-black"
              style={{
                aspectRatio: demo.width && demo.height ? `${demo.width} / ${demo.height}` : "16 / 9",
                maxHeight: "78vh",
                width: demo.width && demo.height && demo.height > demo.width ? "auto" : "100%",
                maxWidth: "100%",
              }}
            >
              <video
                data-testid="demo-video"
                controls
                playsInline
                preload="auto"
                poster={demo.has_poster ? `${API}/demos/${demo.slug}/poster` : undefined}
                className="w-full h-full block"
                src={`${API}/demos/${demo.slug}/video`}
              />
            </div>
          ) : (
            <div className="border border-border p-14 text-center" data-testid="demo-pending">
              <p className="text-muted-foreground">
                This film is being prepared. Please check back shortly.
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-muted-foreground tracking-wide">
          <span>Form Pathway - Cinematic Property Films</span>
          <a href="mailto:hello@formpathway.com" className="hover:text-foreground transition-colors">
            hello@formpathway.com
          </a>
        </div>
      </footer>
    </div>
  );
}
